import * as React from "react"
import { Button, Col, Form, Row, Spinner, Table } from "react-bootstrap"
import { BehaviorSubject, concat, of, Subscription } from "rxjs"
import { debounceTime, filter, map, switchMap } from "rxjs/operators"
import { toast } from "react-toastify"
import { ImportCSVMaxFileSize } from "../../../config/Constants"
import ConfirmationModal from "../../ConfirmationModal"
import MappingFieldsGetObservable, { MappingField, MappingFields } from "../../../api/Randoms/Import/MappingFieldsGet"
import ImportJobData from "../../../api/Administration/CompanyImport/Model/ImportJobData"
import ImportJobCreateObservable from "../../../api/Randoms/Import/ImportJobCreate"
import ImportJobMappingSetObservable from "../../../api/Randoms/Import/ImportJobMappingSet"
import ImportJobValidateObservable from "../../../api/Randoms/Import/ImportJobValidate"
import ImportJobStartObservable from "../../../api/Randoms/Import/ImportJobStart"
import ImportJobCancelObservable from "../../../api/Randoms/Import/ImportJobCancel"
import ImportJobValidationResultGetObservable, { ImportJobValidationResult } from "../../../api/Randoms/Import/ImportJobValidationResultGet"
import AutoFilterCompany from "../../../api/Companies/CompanyModel/AutoFilterCompany"
import AutoFilterCompaniesGetObservable from "../../../api/Companies/AutoFilterCompaniesGet"
import CompanyInput from "../../FormattedInputs/CompanyInput"
import ImportErrorDetails from "../ImportErrorDetails"
import FileSaver from "file-saver"

interface ImportPoolMembersFieldMapping {
  column: number
  fieldGroup?: string
  mappingField?: MappingField
}

interface ImportPoolMembersProps {
  poolID: number,
  isGovt?: boolean
  poolPrimaryIDType?: string
  isConsortium?: boolean
  multiplePools?: boolean
  onUpdate?: (updated: boolean) => void
}

interface ImportPoolMembersState {
  step: "file" | "mapping" | "import"
  fileInputKey: string
  selectedCompany?: AutoFilterCompany
  fileName?: string
  fileContent?: string
  fileHasHeaderRow: boolean
  importData?: ImportJobData
  mappingFields?: MappingFields
  mapping?: ImportPoolMembersFieldMapping[]
  validationResult?: ImportJobValidationResult
  unfilteredCompanies?: AutoFilterCompany[]
  companies: AutoFilterCompany[]
  isCompaniesLoading: boolean
  isSaving: boolean
  isCancelling: boolean
  showCancelConfirmModal: boolean
  showImportConfirmModal: boolean
}

export default class ImportPoolMembers extends React.Component<ImportPoolMembersProps, ImportPoolMembersState> {
  emptyData: ImportPoolMembersState = {
    step: "file",
    fileInputKey: "file_input_key",
    selectedCompany: undefined,
    fileName: undefined,
    fileContent: undefined,
    fileHasHeaderRow: true,
    importData: undefined,
    mappingFields: undefined,
    mapping: undefined,
    validationResult: undefined,
    companies: [],
    isCompaniesLoading: false,
    isSaving: false,
    isCancelling: false,
    showCancelConfirmModal: false,
    showImportConfirmModal: false
  }
  state: ImportPoolMembersState = { ...this.emptyData, unfilteredCompanies: [] }

  searchCompany$ = new BehaviorSubject<string | undefined>(undefined)
  companiesListSubscription?: Subscription
  searchCompanySubscription?: Subscription
  importJobCreateSubscription?: Subscription
  mappingFieldsGetSubscription?: Subscription
  importJobValidateSubscription?: Subscription
  importJobValidationResultGetSubscription?: Subscription
  importJobMappingSetSubscription?: Subscription
  importJobStartSubscription?: Subscription
  importJobCancelSubscription?: Subscription
  poolCriteriaSubscription?: Subscription

  componentDidMount() {
    if (!!this.props.isConsortium) return
    this.setState({ isCompaniesLoading: true })
    let companies: AutoFilterCompany[] = []
    this.companiesListSubscription = AutoFilterCompaniesGetObservable("").subscribe(
      (data: AutoFilterCompany[]) => {
        companies = data
        this.setState({
          companies: data,
          unfilteredCompanies: data,
          selectedCompany: data.length === 1 ? data[0] : undefined
        })
      },
      () => {
        this.setState({ isCompaniesLoading: false })
      }
    )

    this.searchCompanySubscription = this.searchCompany$.pipe(
      filter(filter => filter !== undefined),
      debounceTime(500),
      map(filter => (filter ?? "").trim()),
      switchMap((filter) => filter.length < 3
        ? of({ isLoading: false, items: this.state.unfilteredCompanies })
        : concat(
          of({ isLoading: true }),
          AutoFilterCompaniesGetObservable(filter).pipe(
            map((data) => ({ isLoading: false, items: data }))
          )
        ))
    ).subscribe(
      (state: { isLoading: boolean, items?: AutoFilterCompany[] }) => {
        state.items
          ? this.setState({ isCompaniesLoading: state.isLoading, companies: state.items })
          : this.setState({ isCompaniesLoading: state.isLoading })
      },
      () => this.setState({ isCompaniesLoading: false })
    )
  }

  componentWillUnmount() {
    this.companiesListSubscription && this.companiesListSubscription.unsubscribe()
    this.searchCompanySubscription && this.searchCompanySubscription.unsubscribe()
    this.importJobCreateSubscription && this.importJobCreateSubscription.unsubscribe()
    this.mappingFieldsGetSubscription && this.mappingFieldsGetSubscription.unsubscribe()
    this.importJobValidateSubscription && this.importJobValidateSubscription.unsubscribe()
    this.importJobValidationResultGetSubscription && this.importJobValidationResultGetSubscription.unsubscribe()
    this.importJobMappingSetSubscription && this.importJobMappingSetSubscription.unsubscribe()
    this.importJobStartSubscription && this.importJobStartSubscription.unsubscribe()
    this.importJobCancelSubscription && this.importJobCancelSubscription.unsubscribe()
    this.poolCriteriaSubscription && this.poolCriteriaSubscription.unsubscribe()
  }

  createMapping = (importData: ImportJobData): ImportPoolMembersFieldMapping[] => {
    const colsCount = importData.Records.length > 0 ? Math.max(importData.HeaderItems?.length ?? 0, Math.max(...importData.Records.map(row => row.length))) : 0
    return [...Array(colsCount)].map((_, i) => ({ column: i }))
  }

  handleApiError = (error: Error) => {
    this.setState({ isSaving: false, isCancelling: false })
    toast.error(error)
  }

  handleFileStepNext = () => {
    if (this.state.fileContent) {
      this.setState({ isSaving: true })
      this.importJobCreateSubscription && this.importJobCreateSubscription.unsubscribe()
      this.importJobCreateSubscription = ImportJobCreateObservable(this.state.fileContent, this.state.fileName || "", !!this.state.fileHasHeaderRow, this.state.selectedCompany?.AccountID || 0, this.props.isGovt, this.props.poolID, !!this.props.multiplePools).subscribe(
        (data) => this.getMappingFields(data),
        (error) => this.handleApiError(error)
      )
    }
  }

  getMappingFields = (importJobData: ImportJobData) => {
    this.mappingFieldsGetSubscription && this.mappingFieldsGetSubscription.unsubscribe()
    this.mappingFieldsGetSubscription = MappingFieldsGetObservable(importJobData.ImportJobID).subscribe(
      (mappingFields) => this.setState({
        step: "mapping",
        importData: importJobData,
        mappingFields: mappingFields,
        mapping: this.createMapping(importJobData),
        isSaving: false
      }),
      (error) => this.handleApiError(error)
    )
  }

  handleMappingValidate = () => {
    const selectedMappingFields = this.state.mapping?.map(item => item.mappingField?.UniqueName || "") || []
    if (selectedMappingFields.every(item => item.trim().length === 0)) {
      toast.error("Mapping is empty, select at least one Column map.")
      return
    }
    this.setState({ isSaving: true })
    this.importJobMappingSetSubscription && this.importJobMappingSetSubscription.unsubscribe()
    this.importJobMappingSetSubscription = ImportJobMappingSetObservable(this.state.importData?.ImportJobID || 0, selectedMappingFields).subscribe(
      (mappingResult) => this.startImportValidation(mappingResult.ImportJobID, this.props.poolID),
      (error) => this.handleApiError(error)
    )
  }

  startImportValidation = (importJobID: number, poolID?: number) => {
    this.importJobValidateSubscription && this.importJobValidateSubscription.unsubscribe()
    this.importJobValidateSubscription = ImportJobValidateObservable(importJobID, poolID).subscribe(
      (validationResult) => this.getImportValidationResults(validationResult.ImportJobID),
      (error) => this.handleApiError(error)
    )
  }

  getImportValidationResults = (importJobID: number, poolID?: number) => {
    this.importJobValidationResultGetSubscription && this.importJobValidationResultGetSubscription.unsubscribe()
    this.importJobValidationResultGetSubscription = ImportJobValidationResultGetObservable(importJobID, 1, 10, poolID).subscribe(
      (validationErrors) => this.setState({ step: "import", validationResult: validationErrors, isSaving: false }),
      (error) => this.handleApiError(error)
    )
  }

  performImport = () => {
    this.setState({ isSaving: true })
    this.importJobStartSubscription && this.importJobStartSubscription.unsubscribe()
    this.importJobStartSubscription = ImportJobStartObservable(this.state.importData?.ImportJobID || 0, this.props.poolID).subscribe(
      (data) => {
        if (data.Result && data.ErrorsData.length === 0) {
          toast.success("Records imported successfully.")
        }
        else {
          toast.error("Records imported with errors.")
          if (data.ErrorsData.length > 0) this.export(data.ErrorsData, this.state.fileName || "data.csv")
        }
        this.props.onUpdate && this.props.onUpdate(true)
        this.resetState()
      },
      (error) => this.handleApiError(error)
    )
  }

  export(csv: string, fileName: string) {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, fileName)
    toast.success("CSV file downloaded successfully.")
  }

  performCancel = () => {
    this.setState({ isCancelling: true })
    this.importJobCancelSubscription && this.importJobCancelSubscription.unsubscribe()
    this.importJobCancelSubscription = ImportJobCancelObservable(this.state.importData?.ImportJobID || 0).subscribe(
      () => {
        toast.success("Created job is cancelled successfully.")
        this.resetState()
      },
      (error) => this.handleApiError(error)
    )
  }

  resetState = () => this.setState(prevState => ({
    ...this.emptyData,
    selectedCompany: prevState.companies.length <= 1 ? prevState.selectedCompany : undefined,
    fileInputKey: Math.random().toString()
  }))

  handleHandleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target && e.target.files && e.target.files[0]
    if (file && file.size > 0 && file.size < ImportCSVMaxFileSize) {
      const ext = file.name.substring(file.name.lastIndexOf(".") + 1)
      if (ext && ext.toLowerCase() === "csv") {
        var reader = new FileReader()
        const self = this
        reader.onload = function (e) {
          const result = e.target?.result as string || ""
          const stringLength = result.indexOf("base64") + 7
          const fileContent = result.slice(stringLength, result.length)
          if (fileContent) {
            self.setState({ fileName: file.name, fileContent: fileContent })
          } else {
            self.setState({ fileName: undefined, fileContent: undefined })
            toast.error("CSV file is empty.")
          }
        }
        reader.onerror = function (error) { toast.error(error) }
        reader.readAsDataURL(file)
      } else {
        toast.error("File type not allowed, Please upload CSV file.")
      }
    } else {
      this.setState({ fileName: undefined, fileContent: undefined })
    }
  }

  handleFieldMappingChange = (e: React.ChangeEvent<HTMLSelectElement>, target: ImportPoolMembersFieldMapping, value: string) => {
    const mappings = [...this.state.mapping || []]
    const index = mappings.findIndex(item => item.column === target.column)
    if (index === -1) {
      return
    }
    if (value === "skip") {
      mappings[index] = { column: target.column }
    } else {
      const valueKeys = value.split("|||")
      if (valueKeys.length !== 2) {
        return
      }
      const mappingField = this.state.mappingFields![valueKeys[0] as keyof MappingFields].find(item => item.UniqueName === valueKeys[1])
      if (!mappingField) {
        return
      }
      mappings[index] = { column: target.column, fieldGroup: valueKeys[0], mappingField: mappingField }
    }
    this.setState({ mapping: mappings })
  }
  handleCompanyChange = (items: AutoFilterCompany[]) => {
    const comp = items[0]
    if (!comp) {
      this.setState({ selectedCompany: undefined })
      return
    }
    let ind = -1

    if (this.state.companies) {
      ind = this.state.companies.findIndex(item => item.AccountID === comp.AccountID)
    }
    if (ind < 0) {
      this.setState({ selectedCompany: undefined })
      return
    }
    const selected = this.state.companies[ind]
    this.setState({ selectedCompany: selected })
  }

  render() {
    const usedMappingFields = this.state.step === "mapping" && this.state.mapping
      ? this.state.mapping.filter(item => !!item.mappingField).map(item => item.mappingField!.UniqueName)
      : []
    const poolPrimaryIDType = ((this.props.poolID > 0) && (!!this.props.poolPrimaryIDType)) ? this.props.poolPrimaryIDType : "Unknown"
    const companyPrimaryIDType = this.state.selectedCompany?.PrimaryIDType || "Unknown"
    const isConsortium = !!this.props.isConsortium
    return <div className="ImportPoolMembers">
      <Form hidden={this.state.step !== "file"}>
        <Form.Row><Col><h5>UPLOAD FILE</h5></Col></Form.Row>
        {!isConsortium && !this.props.multiplePools && <Form.Row className="form-group">
          <Form.Label column sm={3}>Company:</Form.Label>
          <Col md={5} className="form-label col-form-label">
            <CompanyInput
              disabled={this.state.unfilteredCompanies && this.state.unfilteredCompanies.length <= 1}
              allowedCompanies={isConsortium? this.state.companies : undefined}
              onChange={items => this.handleCompanyChange(items)} />
          </Col>
        </Form.Row>}
        <Form.Row><Col><h6>Import CSV File</h6></Col></Form.Row>
        <Form.Row className="form-group">
          <Form.Label column sm={3}>Upload file:</Form.Label>
          <Col md={5} className="form-label col-form-label">
            <input type="file" key={this.state.fileInputKey} accept=".csv" onChange={this.handleHandleFileUpload} />
          </Col>
        </Form.Row>
        <Form.Row className="form-group">
          <Col><Form.Check id="fileHasHeader" label="File has header row" checked={this.state.fileHasHeaderRow} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({ fileHasHeaderRow: e.target.checked })} /></Col>
        </Form.Row>
        <Form.Row className="buttons-block">
          <Button type="reset" variant="secondary" className="float-left fixed-w5" onClick={this.resetState} disabled={this.state.isSaving}>RESET</Button>
          <Button variant="primary" className="float-right fixed-w5" onClick={this.handleFileStepNext} disabled={!this.state.fileContent || this.state.isSaving || (!isConsortium && !this.props.multiplePools && !this.state.selectedCompany)}>
            {this.state.isSaving ? <Spinner animation="border" size="sm" /> : "NEXT"}
          </Button>
        </Form.Row>
      </Form>
      <Form hidden={this.state.step !== "mapping"}>
        <Row><Col><h5>FIELD MAPPINGS FOR: {this.state.selectedCompany?.Name} </h5></Col></Row>
        <Row className="form-group">
          <Col><h6>Company Primary ID Type: {companyPrimaryIDType}</h6></Col>
          <Col><h6>Pool Primary ID Type: {poolPrimaryIDType}</h6></Col>
          <Col><h6></h6></Col>
        </Row>
        {this.state.mapping && this.state.mappingFields &&
          <Table className="mappings-table" responsive bordered>
            <thead>
              <tr>
                {this.state.mapping.map(mapping => {
                  const selectedValue = mapping.fieldGroup && mapping.mappingField?.UniqueName ? `${mapping.fieldGroup}|||${mapping.mappingField?.UniqueName}` : ""
                  return (<th key={`h_${mapping.column}`}>
                    <Form.Control as="select" value={selectedValue} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.handleFieldMappingChange(e, mapping, e.target.value)}>
                      <option disabled hidden value="">Skipped Field</option>
                      <option className="map-field-id" disabled>Map Field #{mapping.column + 1}</option>
                      <option value="skip">Set this field as skipped</option>
                      {Object.keys(this.state.mappingFields!).filter(group => this.state.mappingFields![group as keyof MappingFields].length > 0).map(optGroupName =>
                        <optgroup key={`h_${mapping.column}_${optGroupName}`} label={optGroupName}>
                          {this.state.mappingFields![optGroupName as keyof MappingFields].map(field =>
                            <option
                              key={`h_${mapping.column}_${optGroupName}_${field.UniqueName}`}
                              value={`${optGroupName}|||${field.UniqueName}`}
                              disabled={usedMappingFields.includes(field.UniqueName)}>
                              {field.Name}
                            </option>)}
                        </optgroup>)}
                    </Form.Control>
                  </th>)
                })}
              </tr>
            </thead>
            <tbody>
              {this.state.importData && this.state.importData.HeaderItems.length > 0 && <tr>{this.state.mapping!.map(mapping => <td key={`hr_${mapping.column}`}>{this.state.importData!.HeaderItems[mapping.column]}</td>)}</tr>}
              {this.state.importData && this.state.importData.Records.length > 0 && this.state.importData.Records.map((row, i) => <tr key={`d_${i}`}>{this.state.mapping!.map(mapping => <td key={`d_${mapping.column}_${i}`}>{row[mapping.column]}</td>)}</tr>)}
              {this.state.importData && this.state.importData.Records.length === 0 && <tr><td colSpan={this.state.mapping.length}>NO DATA FOUND</td></tr>}
            </tbody>
          </Table>}
        <Form.Row className="buttons-block">
          <Button variant="secondary" className="fixed-w5" onClick={() => this.setState({ step: "file" })} disabled={this.state.isSaving}>BACK</Button>
          <Button variant="primary" className="fixed-w5" onClick={this.handleMappingValidate} disabled={this.state.isSaving}>
            {this.state.isSaving ? <Spinner animation="border" size="sm" /> : "SUBMIT FOR VALIDATION"}
          </Button>
        </Form.Row>
      </Form>
      <Form hidden={this.state.step !== "import"}>
        <Row><Col><h5>IMPORT FILE VALIDATION FOR: {this.state.selectedCompany?.Name}</h5></Col></Row>
        {this.state.validationResult &&
          <ImportErrorDetails importJobID={this.state.validationResult.ImportJobID} errors={this.state.validationResult} />}
        <Form.Row className="buttons-block">
          <Button variant="secondary" className="fixed-w5" onClick={() => this.setState({ step: "mapping", validationResult: undefined })} disabled={this.state.isSaving || this.state.isCancelling}>BACK</Button>
          <Button variant="secondary" className="fixed-w5" onClick={() => this.setState({ showCancelConfirmModal: true })}>
            {this.state.isCancelling ? <Spinner animation="border" size="sm" /> : "CANCEL"}
          </Button>
          <Button variant="primary" className="fixed-w5" onClick={() => this.setState({ showImportConfirmModal: true })} disabled={!this.state.validationResult?.CanBeImported || this.state.isSaving || this.state.isCancelling}>
            {this.state.isSaving ? <Spinner animation="border" size="sm" /> : "IMPORT"}
          </Button>
        </Form.Row>
      </Form>
      <ConfirmationModal show={this.state.showCancelConfirmModal}
        title="CANCEL CONFIRMATION"
        textTitle={this.state.validationResult?.CancelImportMessage}
        text=""
        onConfirm={this.performCancel}
        onHide={() => this.setState({ showCancelConfirmModal: false })} />
      <ConfirmationModal show={this.state.showImportConfirmModal}
        title="IMPORT CONFIRMATION"
        textTitle={this.state.validationResult?.ImportMessage}
        text=""
        onConfirm={this.performImport}
        onHide={() => this.setState({ showImportConfirmModal: false })} />

    </div>
  }

  static defaultProps = { step: "file" }
}
